import React, { useState, useRef, useEffect } from "react";
import type { ItineraryDay } from "@typings/index";

interface InteractiveTimelineProps {
  itinerary: ItineraryDay[];
}

const InteractiveTimeline: React.FC<InteractiveTimelineProps> = ({
  itinerary,
}) => {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);
  const activityCardRef = useRef<HTMLDivElement>(null);

  const handleDayClick = (dayNumber: number) => {
    setSelectedDay(selectedDay === dayNumber ? null : dayNumber);
  };

  // Scroll to activity card on desktop when selectedDay changes
  useEffect(() => {
    if (selectedDay !== null && window.innerWidth >= 1024) {
      activityCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedDay]);

  const handleDayHover = (dayNumber: number | null) => {
    setHoveredDay(dayNumber);
  };

  const ActivityCard: React.FC<{ day: ItineraryDay }> = ({ day }) => (
    <div className="bg-white rounded-lg shadow-lg p-6 animate-timeline-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-primaryText">
          Day {day.dayNumber}: {day.title}
        </h3>
        <button
          onClick={() => setSelectedDay(null)}
          className="cursor-pointer text-gray-500 hover:text-primaryText transition-colors"
          aria-label="Close activity card"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {day.activities.map((activity, index) => (
          <div key={index} className="border-l-4 border-primary pl-4">
            <h4 className="font-semibold text-primaryText">{activity.name}</h4>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Desktop Timeline */}
      <div className="hidden lg:block">
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary"></div>

          {/* Timeline Items */}
          <div className="relative">
            {itinerary.map((day, index) => (
              <div
                key={day.dayNumber}
                className="relative flex items-center mb-8"
              >
                {/* Day Node */}
                <div
                  className={`absolute left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full border-4  cursor-pointer transition-all duration-300 z-10 ${
                    selectedDay === day.dayNumber ||
                    hoveredDay === day.dayNumber
                      ? "bg-secondary scale-115 text-primaryText border-primary"
                      : "bg-primary hover:bg-secondary hover:text-primaryText border-white"
                  }`}
                  onClick={() => handleDayClick(day.dayNumber)}
                  onMouseEnter={() => handleDayHover(day.dayNumber)}
                  onMouseLeave={() => handleDayHover(null)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleDayClick(day.dayNumber);
                    }
                  }}
                  aria-label={`View Day ${day.dayNumber} activities`}
                >
                  <span
                    className={` font-bold text-sm flex items-center justify-center h-full ${
                      selectedDay === day.dayNumber ||
                      hoveredDay === day.dayNumber
                        ? "text-primary"
                        : "text-white"
                    }`}
                  >
                    {day.dayNumber}
                  </span>
                </div>

                {/* Day Title */}
                <div
                  className={`w-1/2 ${
                    index % 2 === 0 ? "pr-8 text-right" : "pl-8 ml-auto"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold ${
                      selectedDay === day.dayNumber
                        ? "text-primary"
                        : "text-primaryText"
                    } cursor-pointer hover:text-primary transition-colors`}
                    onClick={() => handleDayClick(day.dayNumber)}
                  >
                    Day {day.dayNumber}: {day.title}
                  </h3>
                </div>

                {/* Hover Card (Desktop) */}
                {hoveredDay === day.dayNumber && selectedDay === null && (
                  <div
                    className={`absolute top-16 w-80 bg-white rounded-lg shadow-xl p-4 z-20 animate-fade-in ${
                      index % 2 === 0 ? "right-0" : "left-0"
                    }`}
                    onMouseEnter={() => handleDayHover(day.dayNumber)}
                    onMouseLeave={() => handleDayHover(null)}
                  >
                    <h4 className="font-bold text-primaryText mb-2">
                      Day {day.dayNumber}: {day.title}
                    </h4>
                    <div className="space-y-2">
                      {day.activities.slice(0, 2).map((activity, actIndex) => (
                        <div key={actIndex} className="text-sm text-gray-700">
                          {activity.name}
                        </div>
                      ))}
                      {day.activities.length > 2 && (
                        <div className="text-sm text-gray-500">
                          +{day.activities.length - 2} more activities
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Selected Day Card (Desktop) */}
        {selectedDay !== null && (
          <div ref={activityCardRef} className="mt-12">
            <ActivityCard
              day={itinerary.find((d) => d.dayNumber === selectedDay)!}
            />
          </div>
        )}
      </div>

      {/* Mobile Timeline */}
      <div className="lg:hidden">
        <div className="space-y-4">
          {itinerary.map((day) => (
            <div key={day.dayNumber} className="border-l-4 border-primary pl-4">
              <button
                className="w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded"
                onClick={() => handleDayClick(day.dayNumber)}
              >
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <h3 className="font-semibold text-primaryText">
                      Day {day.dayNumber}: {day.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {day.activities.length} activities
                    </p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-primary transform transition-transform duration-300 ${
                      selectedDay === day.dayNumber ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Mobile Activity Card */}
              {selectedDay === day.dayNumber && (
                <div className="mt-4 animate-slide-down">
                  <ActivityCard day={day} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveTimeline;
